// ProductCommission.jsx - WITH COMPANY WISE FILTERING
import { useEffect, useState } from "react";
import { 
  Container, 
  Table, 
  Spinner, 
  Badge, 
  Card, 
  Form, 
  Row, 
  Col,
  Button 
} from "react-bootstrap";
import { motion } from "framer-motion";
import { 
  FaBox, 
  FaBuilding, 
  FaRupeeSign, 
  FaFilter, 
  FaTimes,
  FaSearch,
  FaChartPie
} from "react-icons/fa";
import Header from "../../component/header/header";
import Sidebar from "../../component/sidebar/sidebar";
import "./ProductCommission.css";

const API_BASE = process.env.REACT_APP_API_URL;

const ProductCommission = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [companies, setCompanies] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [minCommission, setMinCommission] = useState("");
  const [maxCommission, setMaxCommission] = useState("");

  const token = localStorage.getItem("token");

  useEffect(() => {
    fetch(`${API_BASE}/dashboard/product-wise-commission`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        return res.json();
      })
      .then((res) => {
        const productData = Array.isArray(res) ? res : [];
        setData(productData);
        setFilteredData(productData);
        
        // Extract unique companies
        const uniqueCompanies = [...new Set(
          productData
            .map(p => p.company || "N/A")
            .filter(c => c && c !== "N/A")
        )].sort();
        setCompanies(uniqueCompanies);
        
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching product commission:", err);
        setError(err.message);
        setLoading(false);
      });
  }, [token]);

  // Apply filters whenever dependencies change
  useEffect(() => {
    let result = [...data];
    
    // Filter by company
    if (selectedCompany) {
      result = result.filter(p => (p.company || "N/A") === selectedCompany);
    }
    
    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      result = result.filter(p => 
        p.productName?.toLowerCase().includes(term) ||
        p.name?.toLowerCase().includes(term) ||
        p.category?.toLowerCase().includes(term) ||
        (p.company || "").toLowerCase().includes(term)
      );
    }
    
    // Filter by commission range
    if (minCommission) {
      const min = parseFloat(minCommission);
      result = result.filter(p => {
        const commissionRate = p.totalSales > 0 ? (p.adminCommission / p.totalSales) * 100 : 0;
        return commissionRate >= min;
      });
    }
    
    if (maxCommission) {
      const max = parseFloat(maxCommission);
      result = result.filter(p => {
        const commissionRate = p.totalSales > 0 ? (p.adminCommission / p.totalSales) * 100 : 0;
        return commissionRate <= max;
      });
    }
    
    setFilteredData(result);
  }, [data, selectedCompany, searchTerm, minCommission, maxCommission]);

  const clearFilters = () => {
    setSelectedCompany("");
    setSearchTerm("");
    setMinCommission("");
    setMaxCommission("");
    setShowFilters(false);
  };

  // Calculate totals
  const totals = filteredData.reduce(
    (acc, item) => ({
      totalQty: acc.totalQty + (item.totalQty || 0),
      totalSales: acc.totalSales + (item.totalSales || 0),
      totalCommission: acc.totalCommission + (item.adminCommission || 0),
      totalVendorEarnings: acc.totalVendorEarnings + (item.vendorEarnings || 0),
    }),
    { totalQty: 0, totalSales: 0, totalCommission: 0, totalVendorEarnings: 0 }
  );

  // Get company statistics
  const getCompanyStats = (company) => {
    const companyProducts = data.filter(p => (p.company || "N/A") === company);
    return {
      count: companyProducts.length,
      totalSales: companyProducts.reduce((sum, p) => sum + (p.totalSales || 0), 0),
      totalCommission: companyProducts.reduce((sum, p) => sum + (p.adminCommission || 0), 0),
    };
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="admin-layout">
          <Sidebar />
          <main className="admin-content">
            <Container fluid>
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">Loading product commission data...</p>
              </div>
            </Container>
          </main>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <div className="admin-layout">
          <Sidebar />
          <main className="admin-content">
            <Container fluid>
              <div className="alert alert-danger">
                <h5>Error Loading Data</h5>
                <p>{error}</p>
              </div>
            </Container>
          </main>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="admin-layout">
        <Sidebar />
        <main className="admin-content">
          <Container fluid>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {/* Header */}
              <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap">
                <div>
                  <h4 className="mb-0">
                    <FaBox className="me-2 text-primary" />
                    Product Wise Commission
                    <Badge bg="info" pill className="ms-3 px-3 py-2">
                      {filteredData.length} Products
                    </Badge>
                    {selectedCompany && (
                      <Badge bg="primary" className="ms-2 px-3 py-2">
                        <FaBuilding className="me-1" />
                        {selectedCompany}
                      </Badge>
                    )}
                  </h4>
                </div>
                <div>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setShowFilters(!showFilters)}
                    className="me-2"
                  >
                    <FaFilter className="me-1" />
                    Filter
                  </Button>
                  {(selectedCompany || searchTerm || minCommission || maxCommission) && (
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={clearFilters}
                    >
                      <FaTimes className="me-1" />
                      Clear Filters
                    </Button>
                  )}
                </div>
              </div>

              {/* Filter Section */}
              {showFilters && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="filter-section mb-4 p-3 bg-light rounded"
                >
                  <Row>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>
                          <FaSearch className="me-1" /> Search Products
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Search by name, category, company..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>
                          <FaBuilding className="me-1" /> Filter by Company
                        </Form.Label>
                        <Form.Select
                          value={selectedCompany}
                          onChange={(e) => setSelectedCompany(e.target.value)}
                        >
                          <option value="">All Companies</option>
                          {companies.map((company) => {
                            const stats = getCompanyStats(company);
                            return (
                              <option key={company} value={company}>
                                {company} ({stats.count} products)
                              </option>
                            );
                          })}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>
                          <FaChartPie className="me-1" /> Commission Range (%)
                        </Form.Label>
                        <div className="d-flex gap-2">
                          <Form.Control
                            type="number"
                            placeholder="Min %"
                            value={minCommission}
                            onChange={(e) => setMinCommission(e.target.value)}
                            style={{ width: "50%" }}
                          />
                          <Form.Control
                            type="number"
                            placeholder="Max %"
                            value={maxCommission}
                            onChange={(e) => setMaxCommission(e.target.value)}
                            style={{ width: "50%" }}
                          />
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  {/* Company Stats Summary */}
                  {selectedCompany && (
                    <Row className="mt-3">
                      <Col>
                        <div className="company-stats p-2 bg-white rounded border">
                          <small className="text-muted">
                            <strong>{selectedCompany}</strong> - 
                            {getCompanyStats(selectedCompany).count} products, 
                            ₹{getCompanyStats(selectedCompany).totalSales.toFixed(2)} sales, 
                            ₹{getCompanyStats(selectedCompany).totalCommission.toFixed(2)} commission
                          </small>
                        </div>
                      </Col>
                    </Row>
                  )}
                </motion.div>
              )}

              {/* Summary Cards */}
              <div className="row g-3 mb-4">
                <div className="col-md-3 col-6">
                  <Card className="summary-card">
                    <Card.Body>
                      <small className="text-muted">Total Products Sold</small>
                      <h5 className="mb-0">{totals.totalQty}</h5>
                    </Card.Body>
                  </Card>
                </div>
                <div className="col-md-3 col-6">
                  <Card className="summary-card">
                    <Card.Body>
                      <small className="text-muted">Total Sales</small>
                      <h5 className="mb-0 text-success">
                        ₹{totals.totalSales.toFixed(2)}
                      </h5>
                    </Card.Body>
                  </Card>
                </div>
                <div className="col-md-3 col-6">
                  <Card className="summary-card">
                    <Card.Body>
                      <small className="text-muted">Total Commission</small>
                      <h5 className="mb-0 text-warning">
                        ₹{totals.totalCommission.toFixed(2)}
                      </h5>
                    </Card.Body>
                  </Card>
                </div>
                <div className="col-md-3 col-6">
                  <Card className="summary-card">
                    <Card.Body>
                      <small className="text-muted">Vendor Earnings</small>
                      <h5 className="mb-0 text-primary">
                        ₹{totals.totalVendorEarnings.toFixed(2)}
                      </h5>
                    </Card.Body>
                  </Card>
                </div>
              </div>

              {/* Data Table */}
              <Card>
                <Card.Body className="p-0">
                  <div className="table-responsive">
                    <Table responsive hover className="modern-table mb-0">
                      <thead className="bg-light">
                        <tr>
                          <th>#</th>
                          <th>Product Name</th>
                          <th>Category</th>
                          <th>
                            <FaBuilding className="me-1" />
                            Company
                            {selectedCompany && (
                              <Badge bg="primary" className="ms-2">
                                Filtered
                              </Badge>
                            )}
                          </th>
                          <th className="text-end">Qty Sold</th>
                          <th className="text-end">Total Sales (₹)</th>
                          <th className="text-end">Admin Commission (₹)</th>
                          <th className="text-end">Vendor Earning (₹)</th>
                          <th className="text-end">Commission %</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredData.length === 0 ? (
                          <tr>
                            <td colSpan="9" className="text-center py-4 text-muted">
                              {data.length === 0 
                                ? "No products found with delivered orders"
                                : "No products match your filters. Try adjusting your search or filters."}
                              {data.length > 0 && (
                                <div className="mt-2">
                                  <Button 
                                    variant="outline-primary" 
                                    size="sm" 
                                    onClick={clearFilters}
                                  >
                                    Clear Filters
                                  </Button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ) : (
                          filteredData.map((p, i) => {
                            const commissionRate = p.totalSales > 0 
                              ? (p.adminCommission / p.totalSales) * 100 
                              : 0;
                            
                            return (
                              <motion.tr
                                key={p.productId || i}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: i * 0.05 }}
                              >
                                <td>{i + 1}</td>
                                <td>
                                  <strong>{p.productName || p.name || "Unknown"}</strong>
                                  <br />
                                  <small className="text-muted">{p.category || "N/A"}</small>
                                </td>
                                <td>
                                  <Badge bg="light" text="dark">
                                    {p.category || "General"}
                                  </Badge>
                                </td>
                                <td>
                                  <FaBuilding className="me-1 text-secondary" />
                                  {p.company || "N/A"}
                                </td>
                                <td className="text-end">{p.totalQty || 0}</td>
                                <td className="text-end fw-bold">
                                  ₹{(p.totalSales || 0).toFixed(2)}
                                </td>
                                <td className="text-end text-warning fw-bold">
                                  ₹{(p.adminCommission || 0).toFixed(2)}
                                </td>
                                <td className="text-end text-success fw-bold">
                                  ₹{(p.vendorEarnings || 0).toFixed(2)}
                                </td>
                                <td className="text-end">
                                  <Badge 
                                    bg={
                                      commissionRate > 10 ? "danger" :
                                      commissionRate > 5 ? "warning" : 
                                      commissionRate > 0 ? "info" : "secondary"
                                    }
                                  >
                                    {commissionRate.toFixed(1)}%
                                  </Badge>
                                </td>
                              </motion.tr>
                            );
                          })
                        )}
                      </tbody>
                      {filteredData.length > 0 && (
                        <tfoot className="table-footer fw-bold bg-light">
                          <tr>
                            <td colSpan="4" className="text-end">Totals:</td>
                            <td className="text-end">{totals.totalQty}</td>
                            <td className="text-end text-success">₹{totals.totalSales.toFixed(2)}</td>
                            <td className="text-end text-warning">₹{totals.totalCommission.toFixed(2)}</td>
                            <td className="text-end text-primary">₹{totals.totalVendorEarnings.toFixed(2)}</td>
                            <td className="text-end">
                              {totals.totalSales > 0 
                                ? ((totals.totalCommission / totals.totalSales) * 100).toFixed(1)
                                : 0}%
                            </td>
                          </tr>
                        </tfoot>
                      )}
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </motion.div>
          </Container>
        </main>
      </div>
    </>
  );
};

export default ProductCommission;